import React, { useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Stack, TextField, Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { getGanttSettings, saveGanttSettings } from "./api/bookingGanttApi";
import { shortDate } from "./utils/ganttLayout";

function minusOneDay(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Admin editor for one campus-season: bed capacity over ranges of nights and
 * the minimum guarantee in student weeks. Saving replaces the whole list.
 */
export default function CapacityDialog({ open, centre, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [guarantee, setGuarantee] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!open || !centre) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    getGanttSettings(centre.campusId)
      .then((s) => {
        if (cancelled) return;
        setGuarantee(s?.minGuaranteeStudentWeeks ?? "");
        const ranges = Array.isArray(s?.capacity) ? s.capacity : [];
        setRows(ranges.length
          ? ranges.map((r) => ({ startDate: r.startDate, endDate: r.endDate, beds: String(r.beds), notes: r.notes || "" }))
          : [{ startDate: centre.arrival, endDate: minusOneDay(centre.departure), beds: "", notes: "" }]);
      })
      .catch((e) => !cancelled && setError(e.message || "Could not load the settings"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, centre]);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const addRow = () => setRows((rs) => {
    const last = rs[rs.length - 1];
    const lastNight = minusOneDay(centre.departure);
    const next = last?.endDate ? new Date(`${last.endDate}T00:00:00Z`) : null;
    if (next) next.setUTCDate(next.getUTCDate() + 1);
    const start = next ? next.toISOString().slice(0, 10) : centre.arrival;
    return [...rs, { startDate: start, endDate: lastNight, beds: "", notes: "" }];
  });

  const save = async () => {
    setError("");
    const capacity = [];
    for (const r of rows) {
      if (!r.startDate && !r.endDate && r.beds === "") continue;
      const beds = Number(r.beds);
      if (!r.startDate || !r.endDate || r.beds === "" || !Number.isInteger(beds)) {
        setError("Each range needs a first night, a last night and a whole number of beds.");
        return;
      }
      capacity.push({ startDate: r.startDate, endDate: r.endDate, beds, notes: r.notes || null });
    }
    const g = guarantee === "" || guarantee === null ? null : Number(guarantee);
    if (g !== null && (!Number.isInteger(g) || g < 0)) {
      setError("The minimum guarantee is a whole number of student weeks.");
      return;
    }
    setSaving(true);
    try {
      await saveGanttSettings(centre.campusId, { minGuaranteeStudentWeeks: g, capacity });
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!centre) return null;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {centre.name}: beds and guarantee
        <Typography variant="body2" color="text.secondary">
          Season {shortDate(centre.arrival)} to {shortDate(centre.departure)}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress size={28} /></Box>
        ) : (
          <Stack spacing={2}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField
              label="Minimum guarantee (student weeks)"
              type="number"
              size="small"
              value={guarantee}
              onChange={(e) => setGuarantee(e.target.value)}
              helperText="Student nights ÷ 7. Leave empty if there is no guarantee."
              sx={{ maxWidth: 320 }}
              inputProps={{ min: 0, step: 1 }}
            />
            <Typography variant="subtitle2">Beds available</Typography>
            <Typography variant="body2" color="text.secondary">
              Dates are nights: the last night is the night before people leave. Use 0 beds for nights the
              centre is unavailable. Nights with no range show no availability figure.
            </Typography>
            {rows.map((r, i) => (
              <Stack key={i} direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                <TextField label="First night" type="date" size="small" value={r.startDate || ""}
                  onChange={(e) => setRow(i, { startDate: e.target.value })} InputLabelProps={{ shrink: true }} />
                <TextField label="Last night" type="date" size="small" value={r.endDate || ""}
                  onChange={(e) => setRow(i, { endDate: e.target.value })} InputLabelProps={{ shrink: true }} />
                <TextField label="Beds" type="number" size="small" value={r.beds}
                  onChange={(e) => setRow(i, { beds: e.target.value })} sx={{ width: 110 }}
                  inputProps={{ min: 0, step: 1 }} />
                <TextField label="Notes" size="small" value={r.notes}
                  onChange={(e) => setRow(i, { notes: e.target.value })} sx={{ flex: 1 }}
                  inputProps={{ maxLength: 255 }} />
                <IconButton aria-label="Remove range" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Stack>
            ))}
            <Box>
              <Button startIcon={<AddIcon />} onClick={addRow}>Add range</Button>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || loading}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
